import { useContext, useEffect } from "react";
import { IconType } from "react-icons";
import { useQueryClient } from "react-query";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import {
  Box,
  BoxProps,
  Divider,
  Drawer,
  DrawerContent,
  Flex,
  IconButton,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { useStiggContext } from "@stigg/react-sdk";
import TableFlow from "../../components/TableFlow";
import ThemeToggle from "../../components/ThemeToggle";
import { AuthContext } from "../../providers/Auth";
import { useSidebar } from "../../providers/Sidebar";
import useLocalStorage from "../../hooks/useLocalStorage";
import { typedSxMap } from "../../utils/typedSxMap";
import { checkPermissionAllowed } from "../../utils/verification";
import NavItem from "./components/NavItem";
import { FiChevronsLeft, FiChevronsRight } from "react-icons/fi";
import { HiOutlineDocumentArrowDown } from "react-icons/hi2";
import { IoIosGitNetwork } from "react-icons/io";
import { PiCube, PiDatabase, PiGear, PiPlayCircle, PiQuestion, PiSignOut, PiUsers, PiWallet } from "react-icons/pi";

interface LinkItemProps {
  name: string;
  label: string;
  icon: IconType;
  url: string;
}
const LinkItems: Array<LinkItemProps> = [
  { name: "importers", label: "Importers", icon: PiCube, url: "/importers" },
  { name: "data", label: "Data", icon: PiDatabase, url: "/data" },
  { name: "schemas", label: "Schemas", icon: PiCube, url: "/schemas" },
  { name: "imports", label: "Imports", icon: PiDatabase, url: "/imports" },
  { name: "extractions", label: "Extractions", icon: HiOutlineDocumentArrowDown, url: "/extractions" },
  { name: "reconciliations", label: "Reconciliations", icon: IoIosGitNetwork, url: "/reconciliations" },
];

export default function Sidebar() {
  const { isOpen, onClose } = useDisclosure();

  return (
    <Box minH="100%" bg={useColorModeValue("gray.100", "gray.900")} overflow="auto">
      <SidebarContent onClose={() => onClose} display={{ base: "none", md: "block" }} />
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} returnFocusOnClose={false} onOverlayClick={onClose} size="full">
        <DrawerContent>
          <SidebarContent onClose={onClose} />
        </DrawerContent>
      </Drawer>
    </Box>
  );
}

interface SidebarProps extends BoxProps {
  onClose: () => void;
}

const SidebarContent = ({ onClose }: SidebarProps) => {
  const queryClient = useQueryClient();
  const [storagedNavSize, setStoragedNavSize] = useLocalStorage("navSize", "large");
  const { navSize, setNavSize } = useSidebar();
  const sessionContext = useContext(AuthContext);
  const { accessTokenPayload, sessionExists, signOut } = sessionContext;
  const location = useLocation();
  const urlPage = location.pathname?.substring(1);
  const breakpointValue = useBreakpointValue({ base: "base", sm: "sm", md: "md", lg: "lg", xl: "xl", "2xl": "2xl" });
  const { stigg } = useStiggContext();
  const { hasAccess: hasPipelinesAccess } = stigg.getBooleanEntitlement({ featureId: "feature-pipelines" });
  const { hasAccess: hasExperimentationAccess } = stigg.getBooleanEntitlement({ featureId: "feature-experimentation" });
  const isCollapsed = navSize === "small";

  useEffect(() => {
    if (!isCollapsed && (breakpointValue === "sm" || breakpointValue === "md")) {
      changeNavSize("small");
    }
  }, [breakpointValue]);

  useEffect(() => {
    setStoragedNavSize(navSize);
  }, [navSize]);

  const changeNavSize = (newSize: string) => {
    setNavSize(newSize);
  };

  async function onLogout() {
    queryClient.clear();
    signOut && signOut();
  }

  const styles = typedSxMap({
    container: {
      position: "sticky",
      left: "5",
      h: "100vh",
      w: isCollapsed ? "75px" : "250px",
      flexDir: "column",
      justifyContent: "space-between",
      transition: "width 0.3s ease",
      button: {
        fontFamily: "Inter, sans-serif",
        color: "var( --color-text-button-sidebar)",
        WebkitFontSmoothing: "auto",
      },
    },
    topMenuContainer: {
      mt: 2,
      p: "2",
      flexDir: "column",
      w: "100%",
      alignItems: isCollapsed ? "center" : "flex-start",
    },
    bottomMenuContainer: {
      p: "5%",
      flexDir: "column",
      w: "100%",
      alignItems: isCollapsed ? "center" : "flex-start",
      mb: 2,
    },
    divider: {
      mt: 4,
      mb: 2,
      borderColor: "var(--color-border)",
      borderBottomWidth: "1px",
      borderStyle: "solid",
    },
    themeToggleBox: {
      margin: "2",
    },
    toggleNavSize: {
      fontSize: "xl",
      position: "absolute",
      left: isCollapsed ? "50%" : undefined,
      transform: isCollapsed ? "translateX(-50%)" : undefined,
      right: isCollapsed ? "" : "2",
      padding: "2",
      borderRadius: "10",
      height: "auto",
      minWidth: "auto",
      opacity: isCollapsed ? "0" : "1",
      _hover: {
        backgroundColor: "var(--color-border)",
      },
      _groupHover: {
        backgroundColor: isCollapsed ? "var(--color-border)" : undefined,
        opacity: "1",
      },
    },
    avatar: {
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      width: 4,
      height: 4,
    },
  });
  return (
    <Flex sx={styles.container} role="group">
      <Flex sx={styles.topMenuContainer} as="nav">
        <Flex alignItems="center" mx="2" mb={5}>
          <Link to="/">
            <TableFlow color size="small" short={isCollapsed} />
          </Link>
          <IconButton
            className="toggle-nav-size"
            sx={styles.toggleNavSize}
            background="none"
            aria-label="toggle navigation"
            icon={isCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
            onClick={() => {
              if (isCollapsed) changeNavSize("large");
              else changeNavSize("small");
            }}
          />
        </Flex>

        {!hasPipelinesAccess && (
          <NavItem
            navSize={navSize}
            icon={PiPlayCircle}
            title="Getting started"
            name="getting"
            url="/getting-started"
            isSelected={urlPage.includes("getting")}
          />
        )}

        {LinkItems.filter(({ name }) => {
          if (hasExperimentationAccess && hasPipelinesAccess) {
            return name === "extractions" || name === "imports" || name === "schemas" || name === "reconciliations";
          } else if (hasExperimentationAccess) {
            return name === "extractions" || name === "schemas" || name === "reconciliations";
          } else if (hasPipelinesAccess) {
            return name === "imports" || name === "schemas";
          } else {
            return name !== "extractions" && name !== "imports" && name !== "schemas" && name !== "reconciliations";
          }
        }).map((link) => {
          const linkName = link.name.toLowerCase().split("").slice(0, -1).join("");
          return (
            <NavItem
              key={link.name}
              navSize={navSize}
              icon={link.icon}
              title={link.label}
              url={link.url}
              name={link.name}
              isSelected={urlPage.includes(linkName)}
            />
          );
        })}
      </Flex>

      <Flex sx={styles.bottomMenuContainer}>
        <Divider sx={styles.divider} />
        <Box sx={styles.themeToggleBox}>
          <ThemeToggle small={isCollapsed} />
        </Box>
        {!hasPipelinesAccess && (
          <NavItem navSize={navSize} icon={PiQuestion} title="Docs" name="docs" url="https://tableflow.com/docs" isExternalLink />
        )}
        <>
          {checkPermissionAllowed(sessionExists, accessTokenPayload, "users_manage") && (
            <NavItem navSize={navSize} icon={PiUsers} title="Team" name="users" url="/users" isSelected={urlPage.includes("users")} />
          )}
          {!hasPipelinesAccess && checkPermissionAllowed(sessionExists, accessTokenPayload, "billing_manage") && (
            <NavItem navSize={navSize} icon={PiWallet} title="Billing" name="billing" url="/billing" isSelected={urlPage.includes("billing")} />
          )}
        </>
        <NavItem navSize={navSize} icon={PiGear} title="Settings" name="settings" url="/settings" isSelected={urlPage.includes("settings")} />
        <NavItem navSize={navSize} icon={PiSignOut} title="Logout" name="logout" onClick={() => onLogout()} />
      </Flex>
    </Flex>
  );
};
